/**
 * Animation für Super-Reaktionen (`useUi().bursts`, ausgelöst in realtime/handlers.ts): Funkenflug des Emojis
 * über der betroffenen Nachricht. Bei reduzierter Bewegung (System oder Einstellung) wird nichts gezeigt.
 */
import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { EmojiGlyph } from '../../components/EmojiPicker';
import { useAppStore } from '../../store/app';
import { useUi, type SuperBurst } from '../../store/ui';
import { useEmojiUrl } from './emojiCache';
import { prefersReducedMotion } from './hooks';
import './messages.css';

const PARTICLES = 12;

/** Deterministische Pseudo-Zufallszahlen je Burst (rein, damit das Rendern stabil bleibt). */
function rng(seed: number): () => number {
  let x = (seed * 2654435761) >>> 0 || 1;
  return () => {
    x ^= x << 13;
    x ^= x >>> 17;
    x ^= x << 5;
    return ((x >>> 0) % 10000) / 10000;
  };
}

function burstPosition(messageId: string): { x: number; y: number } | null {
  if (!/^\d{1,20}$/.test(messageId)) return null;
  const el = document.querySelector<HTMLElement>(`[data-testid="message"][data-message-id="${messageId}"]`);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.bottom < 0 || r.top > window.innerHeight) return null;
  return { x: r.left + Math.min(r.width / 2, 160), y: r.top + r.height / 2 };
}

function Burst({ burst }: { burst: SuperBurst }) {
  const [pos] = useState(() => burstPosition(burst.messageId));
  const url = useEmojiUrl(burst.emoji.id);
  const particles = useMemo(() => {
    const rand = rng(burst.id);
    return Array.from({ length: PARTICLES }, (_, i) => {
      const angle = (i / PARTICLES) * Math.PI * 2 + rand() * 0.4;
      const dist = 60 + rand() * 70;
      return {
        dx: Math.cos(angle) * dist,
        dy: Math.sin(angle) * dist - 30,
        rot: Math.round(rand() * 120 - 60),
        delay: Math.round(rand() * 120),
        scale: 0.6 + rand() * 0.6,
      };
    });
  }, [burst.id]);
  if (!pos) return null;
  const emoji = { ...burst.emoji, url };
  return (
    <div className="super-burst" style={{ left: pos.x, top: pos.y }} data-testid="super-burst" data-message-id={burst.messageId}>
      <span className="super-burst-core">
        <EmojiGlyph emoji={emoji} size={56} />
      </span>
      {particles.map((p, i) => (
        <span
          key={i}
          className="super-burst-particle"
          style={
            {
              '--dx': `${p.dx}px`,
              '--dy': `${p.dy}px`,
              '--rot': `${p.rot}deg`,
              '--scale': p.scale,
              animationDelay: `${p.delay}ms`,
            } as React.CSSProperties
          }
        >
          <EmojiGlyph emoji={emoji} size={22} />
        </span>
      ))}
    </div>
  );
}

export function SuperReactionLayer() {
  const bursts = useUi((s) => s.bursts);
  const reducedSetting = useAppStore((s) => s.settings?.reducedMotion);
  if (!bursts.length || prefersReducedMotion(reducedSetting)) return null;
  return createPortal(
    <div className="super-layer" aria-hidden="true">
      {bursts.map((b) => (
        <Burst key={b.id} burst={b} />
      ))}
    </div>,
    document.body,
  );
}
